import { memo } from "react";
import Stack from "@mui/material/Stack";
import CircularProgress from "@mui/material/CircularProgress";
import { styled } from "../../../../lib/styled";
import { Note } from "../../../primitives/data-display/Note";
import { Meta } from "../../../primitives/data-display/Meta";
import { AIQuote } from "../../../primitives/data-display/AIQuote";
import { useAsyncData } from "../../../../hooks/useAsyncData";
import { getDocumentExtractionTrace } from "../../../../lib/corpusApi";
import type {
  DocumentAdmissionTrace,
  DocumentAdmissionFindingAssessment,
  DocumentAdmissionCompatibilityAssessment,
} from "../../../../lib/corpusApi";

// The trace does not carry the verdict (admitted vs rejected), only the deciding step.
// These sentences assume admission context — AdmissionTracePanel is only shown for corpus docs.
// TODO: add verdict to DocumentAdmissionTrace so this can be stated unambiguously.
const STEP_SENTENCE: Record<string, string> = {
  "evidence.deterministic":
    "Admitted automatically — the evidence signals were present and clear enough without AI involvement.",
  "evidence.llm": "Admitted after AI assessed the primary finding and each evidence axis below.",
  "compatibility.deterministic":
    "Compatibility check: the document is written in a language the pipeline cannot process yet.",
  "compatibility.llm":
    "Compatibility check: AI assessed which study types the paper reports its results from, below.",
};

const AXIS_LABEL: Record<string, string> = {
  data_origin: "Data origin",
  health_target: "Health target",
  finding_type: "Finding type",
};

const AxisGrid = styled("div")(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "repeat(3, 1fr)",
  gap: theme.spacing(1),
  [theme.breakpoints.down("sm")]: {
    gridTemplateColumns: "1fr",
  },
}));

const AxisCard = styled("div")(({ theme }) => ({
  padding: theme.spacing(1, 1.25),
  borderRadius: theme.shape.borderRadius,
  backgroundColor: theme.palette.background.default,
}));

const AxisKey = styled("div")(({ theme }) => ({
  fontWeight: 600,
  fontSize: "0.6875rem",
  textTransform: "uppercase",
  letterSpacing: "0.04em",
  color: theme.palette.text.secondary,
}));

const AxisVerdict = styled("div")(({ theme }) => ({
  fontWeight: 600,
  fontSize: "0.6875rem",
  marginTop: theme.spacing(0.25),
  color: theme.palette.success.main,
}));

const AxisJustification = styled("div")(({ theme }) => ({
  marginTop: theme.spacing(0.5),
  color: theme.palette.text.secondary,
  lineHeight: 1.4,
  fontSize: "0.75rem",
}));

interface AdmissionCompatibilityProps {
  compatibility: DocumentAdmissionCompatibilityAssessment;
}

/** What the compatibility step's AI check found about the paper's study types. */
function AdmissionCompatibility({ compatibility }: AdmissionCompatibilityProps) {
  const answer = compatibility.study_type_scope;
  return (
    <AxisCard>
      <AxisKey>Study types reported</AxisKey>
      <AxisVerdict>{answer.value.replace(/_/g, " ")}</AxisVerdict>
      {answer.justification && <AxisJustification>{answer.justification}</AxisJustification>}
    </AxisCard>
  );
}

interface AdmissionAssessmentProps {
  assessment: DocumentAdmissionFindingAssessment;
}

function AdmissionAssessment({ assessment }: AdmissionAssessmentProps) {
  const axes = (["data_origin", "health_target", "finding_type"] as const).map((key) => ({
    label: AXIS_LABEL[key],
    answer: assessment[key],
  }));

  return (
    <Stack gap={1.5}>
      <AIQuote size="small">{assessment.primary_finding}</AIQuote>
      <AxisGrid>
        {axes.map(({ label, answer }) => (
          <AxisCard key={label}>
            <AxisKey>{label}</AxisKey>
            <AxisVerdict>{answer.value}</AxisVerdict>
            {answer.justification && <AxisJustification>{answer.justification}</AxisJustification>}
          </AxisCard>
        ))}
      </AxisGrid>
    </Stack>
  );
}

export interface AdmissionTracePanelBodyProps {
  admission: DocumentAdmissionTrace;
}

export function AdmissionTracePanelBody({ admission }: AdmissionTracePanelBodyProps) {
  const sentence = STEP_SENTENCE[admission.step] ?? admission.step;
  return (
    <Stack gap={2}>
      <Meta>{sentence}</Meta>
      {admission.compatibility && (
        <AdmissionCompatibility compatibility={admission.compatibility} />
      )}
      {admission.assessment && <AdmissionAssessment assessment={admission.assessment} />}
    </Stack>
  );
}

/** Displays corpus admission gate data (deciding step + optional LLM assessment axes) for a
 *  single document. Fetches from the extraction trace endpoint. Shows for all documents,
 *  including escalated ones (where assessment is null). */
export const AdmissionTracePanel = memo(function AdmissionTracePanel({
  sourceId,
}: {
  sourceId: string;
}) {
  const { data, loading, error } = useAsyncData(() => getDocumentExtractionTrace(sourceId));

  if (loading) {
    return (
      <Stack alignItems="center" justifyContent="center" padding={3}>
        <CircularProgress size={24} />
      </Stack>
    );
  }

  if (error) {
    return <Note padding={2}>{error}</Note>;
  }

  if (!data) {
    return <Note padding={2}>No extraction trace recorded for this document.</Note>;
  }

  if (!data.admission) {
    return (
      <Note padding={2}>
        Admission data unavailable — this document predates the admission gate or was escalated
        without a conclusive assessment.
      </Note>
    );
  }

  return (
    <Stack padding={2}>
      <AdmissionTracePanelBody admission={data.admission} />
    </Stack>
  );
});
